import { getPathTenantSlug } from '../config/workspaceHost';

export interface ClientAuthSession {
  username: string;
  tenantId: string;
  employeeId: string | null;
  permissions: string[];
  expiresAt: string;
  /** True when the signed-in employee is a platform operator (cross-tenant staff). */
  isPlatformOperator?: boolean;
}

const LEGACY_STORAGE_KEY = 'authState';
const LEGACY_SESSION_STORAGE_KEY = 'authSession';
const TENANT_STORAGE_KEY = 'authTenantId';
const REFRESH_TOKEN_STORAGE_KEY = 'authRefreshToken';

const scopedStorageKey = (base: string): string => {
  const slug = getPathTenantSlug();
  return slug ? `${base}:${slug}` : base;
};

let activeSession: ClientAuthSession | null = null;
let accessToken: string | null = null;

/**
 * Prefer localStorage so auth survives new tabs.
 * Fall back to sessionStorage and migrate for sessions created before this change.
 */
const readPersistedValue = (key: string): string | null => {
  try {
    const fromLocal = localStorage.getItem(key)?.trim();
    if (fromLocal) {
      return fromLocal;
    }

    const fromSession = sessionStorage.getItem(key)?.trim();
    if (!fromSession) {
      return null;
    }

    localStorage.setItem(key, fromSession);
    sessionStorage.removeItem(key);
    return fromSession;
  } catch {
    return null;
  }
};

const writePersistedValue = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
    sessionStorage.removeItem(key);
  } catch {
    // Ignore.
  }
};

const removePersistedValue = (key: string): void => {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    // Ignore.
  }
};

const getPersistedTenantId = (): string | null => readPersistedValue(scopedStorageKey(TENANT_STORAGE_KEY));

export const getActiveSession = (): ClientAuthSession | null => activeSession;

export const getAccessToken = (): string | null => accessToken?.trim() || null;

export const getRefreshToken = (): string | null => readPersistedValue(scopedStorageKey(REFRESH_TOKEN_STORAGE_KEY));

export const setAuthTokens = (nextAccessToken: string, nextRefreshToken: string): void => {
  accessToken = nextAccessToken.trim();
  writePersistedValue(scopedStorageKey(REFRESH_TOKEN_STORAGE_KEY), nextRefreshToken.trim());
};

export const persistAuthTokensFromPayload = (payload: unknown): void => {
  if (!payload || typeof payload !== 'object') {
    return;
  }

  const record = payload as { accessToken?: unknown; refreshToken?: unknown };
  const nextAccessToken = typeof record.accessToken === 'string' ? record.accessToken.trim() : '';
  const nextRefreshToken = typeof record.refreshToken === 'string' ? record.refreshToken.trim() : '';

  if (nextAccessToken && nextRefreshToken) {
    setAuthTokens(nextAccessToken, nextRefreshToken);
    return;
  }

  if (nextRefreshToken) {
    writePersistedValue(scopedStorageKey(REFRESH_TOKEN_STORAGE_KEY), nextRefreshToken);
  }

  if (nextAccessToken) {
    accessToken = nextAccessToken;
  }
};

export const setActiveSession = (session: ClientAuthSession | null): void => {
  activeSession = session;

  if (session?.tenantId?.trim()) {
    writePersistedValue(scopedStorageKey(TENANT_STORAGE_KEY), session.tenantId.trim());
  } else {
    removePersistedValue(scopedStorageKey(TENANT_STORAGE_KEY));
  }
};

export const purgeLegacyAuthStorage = (): void => {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  } catch {
    // Ignore.
  }
};

export const getBootstrapTenantId = (): string | null =>
  activeSession?.tenantId?.trim() || getPersistedTenantId();

export const clearAuthSession = (): void => {
  activeSession = null;
  accessToken = null;
  purgeLegacyAuthStorage();
  removePersistedValue(scopedStorageKey(TENANT_STORAGE_KEY));
  removePersistedValue(scopedStorageKey(REFRESH_TOKEN_STORAGE_KEY));
};

export const isSessionExpired = (session: ClientAuthSession | null, refreshBufferSeconds = 60): boolean => {
  if (!session?.expiresAt) {
    return true;
  }

  const expiresAtMs = Date.parse(session.expiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return false;
  }

  return expiresAtMs - Date.now() <= refreshBufferSeconds * 1000;
};

export const getAuthRequestContext = (): {
  tenantId: string | null;
  username: string | null;
} => ({
  tenantId: activeSession?.tenantId ?? null,
  username: activeSession?.username ?? null,
});

export const resolveEmployeeIdFromSession = (
  session: ClientAuthSession | null,
  tenantId?: string | null,
): string | null => {
  if (!session) {
    return null;
  }

  if (session.employeeId?.trim()) {
    return session.employeeId;
  }

  const resolvedTenantId = (tenantId || session.tenantId || '').trim().toLowerCase();
  if (!resolvedTenantId) {
    return null;
  }

  return null;
};
